import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Wrench, Clock, MessageSquare, Paperclip, Send, Trash2, Upload, FileText, Eye, ExternalLink, Download } from 'lucide-react';
import api from '../../services/api';
import { PageHeader, EmptyState } from '../../components/PageParts';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { formatCurrency } from '../../utils/format';
import DocumentViewerModal from '../../components/DocumentViewerModal';
import { openFileInNewTab, downloadFile } from '../../utils/fileViewer';

const STATUSES = ['Aberta', 'Aguardando Aprovação', 'Em Andamento', 'Pausada', 'Aguardando Peças', 'Finalizada', 'Cancelada'];

// -------- Comments panel --------
function CommentsPanel({ entityId }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/comments', { params: { entity_type: 'maintenance_order', entity_id: entityId, limit: 100 } });
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post('/comments', { entity_type: 'maintenance_order', entity_id: entityId, text });
      setText(''); load(); toast.success('Comentário enviado');
    } catch { toast.error('Erro ao enviar'); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Excluir comentário?')) return;
    try { await api.delete(`/comments/${id}`); load(); toast.success('Excluído'); }
    catch { toast.error('Erro'); }
  };

  return (
    <div className="space-y-3">
      <form onSubmit={submit} className="rounded-lg border border-neutral-200 dark:border-neutral-800 p-3 space-y-2">
        <Textarea data-testid="os-comment-text" rows={2} placeholder="Escreva um comentário..." value={text} onChange={e => setText(e.target.value)} />
        <div className="flex justify-end">
          <Button data-testid="os-comment-submit" type="submit" size="sm" disabled={saving || !text.trim()} className="bg-brand hover:bg-[#A81E23] text-white">
            <Send className="h-3.5 w-3.5 mr-1" />{saving ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
      </form>
      {loading ? <div className="text-xs text-neutral-500">Carregando...</div> :
        items.length === 0 ? <p className="text-xs text-neutral-500 py-4 text-center">Nenhum comentário ainda</p> :
        <ul data-testid="os-comment-list" className="space-y-2">
          {items.map(c => (
            <li key={c.id} className="rounded-lg bg-neutral-50 dark:bg-neutral-800/50 p-3 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold">{c.created_by_name || 'Usuário'}</span>
                    <span className="text-[10px] text-neutral-500">{new Date(c.created_at).toLocaleString('pt-BR')}</span>
                  </div>
                  <p className="whitespace-pre-wrap">{c.text}</p>
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => del(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

// -------- Attachments panel --------
function AttachmentsPanel({ entityId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/attachments', { params: { entity_type: 'maintenance_order', entity_id: entityId, limit: 100 } });
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Máximo 5MB'); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await api.post('/attachments', {
        entity_type: 'maintenance_order', entity_id: entityId,
        name: name || file.name, description: description || null,
        mime_type: file.type, size: file.size, url: dataUrl,
      });
      setName(''); setDescription(''); load(); toast.success('Arquivo anexado');
    } catch { toast.error('Erro ao anexar'); } finally { setUploading(false); e.target.value = ''; }
  };

  const del = async (id) => {
    if (!window.confirm('Excluir anexo?')) return;
    try { await api.delete(`/attachments/${id}`); load(); toast.success('Excluído'); }
    catch { toast.error('Erro'); }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 p-3 space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Input placeholder="Nome (opcional)" value={name} onChange={e => setName(e.target.value)} className="h-8 text-xs" />
          <Input placeholder="Descrição (opcional)" value={description} onChange={e => setDescription(e.target.value)} className="h-8 text-xs" />
        </div>
        <label className="flex items-center justify-center gap-2 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg py-3 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
          <Upload className="h-4 w-4 text-neutral-500" />
          <span className="text-xs text-neutral-600">{uploading ? 'Enviando...' : 'Clique para selecionar (máx 5MB)'}</span>
          <input data-testid="os-file-input" type="file" className="hidden" onChange={onFile} disabled={uploading} accept="image/*,application/pdf,.doc,.docx" />
        </label>
      </div>
      {loading ? <div className="text-xs text-neutral-500">Carregando...</div> :
        items.length === 0 ? <p className="text-xs text-neutral-500 py-4 text-center">Nenhum anexo</p> :
        <ul data-testid="os-attachment-list" className="space-y-2">
          {items.map(att => (
            <li key={att.id} className="flex items-center gap-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 p-3">
              <FileText className="h-4 w-4 text-neutral-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => {
                    setViewerFile({ url: att.url, name: att.name, title: att.name });
                    setViewerOpen(true);
                  }}
                  className="text-sm font-medium hover:text-brand truncate block text-left"
                  title="Clique para visualizar"
                >
                  {att.name}
                </button>
                <div className="text-[10px] text-neutral-500">
                  {att.mime_type || '-'} • {att.size ? `${(att.size / 1024).toFixed(0)} KB` : ''} • {new Date(att.created_at).toLocaleDateString('pt-BR')}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-neutral-600 hover:text-brand"
                  title="Visualizar documento"
                  onClick={() => {
                    setViewerFile({ url: att.url, name: att.name, title: att.name });
                    setViewerOpen(true);
                  }}
                >
                  <Eye className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-neutral-600 hover:text-brand"
                  title="Abrir em nova guia"
                  onClick={() => openFileInNewTab(att.url, att.name)}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-neutral-600 hover:text-brand"
                  title="Baixar arquivo"
                  onClick={() => downloadFile(att.url, att.name)}
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => del(att.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      }

      <DocumentViewerModal
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fileUrl={viewerFile?.url}
        fileName={viewerFile?.name}
        title={viewerFile?.title}
      />
    </div>
  );
}

export default function MaintenanceOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [os, setOs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [changing, setChanging] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/maintenance-orders/${id}`); setOs(data); }
    catch { toast.error('OS não encontrada'); navigate('/manutencao/os'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const openStatus = (s) => { setNewStatus(s); setStatusNotes(''); setStatusOpen(true); };

  const submitStatus = async () => {
    setChanging(true);
    try {
      await api.post(`/maintenance-orders/${id}/status`, { new_status: newStatus, notes: statusNotes });
      toast.success('Status atualizado'); setStatusOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } finally { setChanging(false); }
  };

  if (loading || !os) return <div><PageHeader title="OS" /><div className="h-96 shimmer rounded-xl" /></div>;

  return (
    <div>
      <button data-testid="back-to-os-list" onClick={() => navigate('/manutencao/os')} className="flex items-center gap-1 text-xs text-neutral-500 hover:text-brand mb-3">
        <ArrowLeft className="h-3 w-3" /> Voltar para OS
      </button>
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-3">
            <span className="font-mono">{os.number}</span>
            <span data-testid="os-current-status" className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-neutral-200 text-neutral-700">{os.os_status}</span>
          </h1>
          <p className="text-sm text-neutral-500 mt-1">Veículo: <span className="font-mono">{os.vehicle_plate}</span> • Prioridade: {os.priority}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUSES.filter(s => s !== os.os_status).map(s => (
            <Button data-testid={`change-status-${s}`} key={s} size="sm" variant="outline" onClick={() => openStatus(s)}>→ {s}</Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
            <h3 className="font-semibold mb-3">Informações técnicas</h3>
            <div className="space-y-2 text-sm">
              <div><span className="text-neutral-500">Defeito informado:</span> <div className="mt-1">{os.reported_defect || '-'}</div></div>
              <div><span className="text-neutral-500">Diagnóstico:</span> <div className="mt-1">{os.diagnosis || '-'}</div></div>
              <div><span className="text-neutral-500">Solução aplicada:</span> <div className="mt-1">{os.solution || '-'}</div></div>
              <div><span className="text-neutral-500">Observações:</span> <div className="mt-1">{os.notes || '-'}</div></div>
            </div>
          </div>

          <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
            <h3 className="font-semibold mb-3">Custos discriminados</h3>
            {os.items?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-neutral-500 text-xs uppercase">
                    <tr><th className="text-left py-2">Tipo</th><th className="text-left py-2">Descrição</th><th className="text-right py-2">Qtd</th><th className="text-right py-2">Unit</th><th className="text-right py-2">Total</th></tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                    {os.items.map((it, i) => (
                      <tr key={i}>
                        <td className="py-2 text-xs">{it.kind}</td>
                        <td className="py-2">{it.description}</td>
                        <td className="py-2 text-right text-xs">{it.quantity}</td>
                        <td className="py-2 text-right text-xs">{formatCurrency(it.unit_value)}</td>
                        <td className="py-2 text-right font-medium">{formatCurrency(it.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td colSpan={4} className="pt-3 text-right font-semibold">Total OS:</td><td className="pt-3 text-right font-bold text-lg">{formatCurrency(os.costs?.total || 0)}</td></tr>
                  </tfoot>
                </table>
              </div>
            ) : <p className="text-xs text-neutral-500">Nenhum item lançado.</p>}
          </div>

          <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
            <Tabs defaultValue="comments">
              <TabsList>
                <TabsTrigger value="comments" data-testid="tab-comments"><MessageSquare className="h-3.5 w-3.5 mr-1.5" />Comentários</TabsTrigger>
                <TabsTrigger value="attachments" data-testid="tab-attachments"><Paperclip className="h-3.5 w-3.5 mr-1.5" />Anexos</TabsTrigger>
              </TabsList>
              <TabsContent value="comments" className="mt-4"><CommentsPanel entityId={id} /></TabsContent>
              <TabsContent value="attachments" className="mt-4"><AttachmentsPanel entityId={id} /></TabsContent>
            </Tabs>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Wrench className="h-4 w-4" />Detalhes</h3>
            <div className="text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-neutral-500">Tipo</span><span>{os.maintenance_type_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Oficina</span><span>{os.workshop || '-'}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Responsável</span><span>{os.responsible_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">KM</span><span>{os.km || '-'}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Preventiva</span><span>{os.is_preventive ? 'Sim' : 'Não'}</span></div>
            </div>
          </div>

          <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Clock className="h-4 w-4" />Fluxo</h3>
            <ol className="relative border-l-2 border-neutral-200 dark:border-neutral-800 ml-2 space-y-3">
              {(os.status_history || []).length === 0 ? <EmptyState icon={Clock} title="Sem histórico" /> : (os.status_history || []).map((h, i) => (
                <li key={i} className="pl-4 relative">
                  <span className="absolute -left-[7px] top-1 h-3 w-3 rounded-full bg-brand" />
                  <div className="text-xs font-medium">{h.status}</div>
                  <div className="text-[10px] text-neutral-500">{new Date(h.at).toLocaleString('pt-BR')} • {h.by}</div>
                  {h.notes && <div className="text-xs text-neutral-600 mt-0.5">{h.notes}</div>}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar status para {newStatus}</DialogTitle>
            <DialogDescription>Registre observações sobre a mudança.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={statusNotes} onChange={e => setStatusNotes(e.target.value)} placeholder="Observações (opcional)" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)}>Cancelar</Button>
            <Button data-testid="confirm-status-change" onClick={submitStatus} disabled={changing} className="bg-brand hover:bg-[#A81E23] text-white">{changing ? 'Aplicando...' : 'Confirmar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
